(() => {
  const HEADER_SELECTOR = 'store-header[data-rmx-header]';
  const MOBILE_QUERY = window.matchMedia('(max-width: 768.98px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  const bool = (value) => String(value) === 'true';
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const parseTime = (value, fallback = '00:00') => {
    const match = String(value || fallback).trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return parseTime(fallback, '00:00');
    return (clamp(Number(match[1]), 0, 23) * 60) + clamp(Number(match[2]), 0, 59);
  };

  const londonNow = () => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(new Date());

    const values = {};
    parts.forEach((part) => {
      if (part.type !== 'literal') values[part.type] = part.value;
    });

    return {
      weekday: values.weekday,
      minutes: ((Number(values.hour) % 24) * 60) + Number(values.minute)
    };
  };

  const initHeader = (header) => {
    if (!header || header.dataset.rmxControllerBound === 'true') return;
    header.dataset.rmxControllerBound = 'true';

    const section = header.closest('[id^="shopify-section-"]');
    const group = header.closest('.shopify-section-group-header-group') || section;
    const mobileMenuToggle = header.querySelector('[data-rmx-mobile-menu-toggle]');
    const mobileMenuLayer = header.querySelector('[data-rmx-mobile-menu-layer]');
    const mobileMenuDrawer = header.querySelector('[data-rmx-mobile-menu-drawer]');
    const searchOverlay = header.querySelector('.header__search');
    const searchInput = header.querySelector('.header__search .js-search-input');
    const searchToggles = Array.from(header.querySelectorAll('.js-show-search, .js-show-search-left'));
    const stickyEnabled = bool(header.dataset.rmxStickyEnabled);
    const smartSticky = bool(header.dataset.rmxSmartSticky);
    const inlineSearch = bool(header.dataset.rmxInlineSearch);

    if (!section || !group) return;
    /* Mark the exact Shopify wrapper that owns this store-header. This keeps
       sticky behaviour away from announcement-bar sections even when Shopify
       changes how section schema classes are applied to the wrapper. */
    group.classList.add('rmx-header-section');
    group.classList.toggle('rmx-header-group--static', !stickyEnabled);

    let offsetFrame = 0;
    let offsetTrackUntil = 0;
    let externalDrawerOpen = false;
    let searchOpener = null;

    const headerGrid = header.querySelector('.header__grid') || header;

    const visibleHeaderBottom = () => {
      const rect = headerGrid.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return 0;
      return clamp(Math.ceil(rect.bottom), 0, window.innerHeight);
    };

    const positionDesktopDropdowns = () => {
      if (MOBILE_QUERY.matches) return;
      const headerBottom = visibleHeaderBottom();
      header.querySelectorAll('.main-nav__child.rmx-compact-dropdown').forEach((dropdown) => {
        const offsetParent = dropdown.offsetParent;
        if (!(offsetParent instanceof HTMLElement)) return;
        const parentTop = offsetParent.getBoundingClientRect().top;
        dropdown.style.setProperty('--rmx-dropdown-top', `${Math.max(0, Math.ceil(headerBottom - parentTop))}px`);
      });
    };

    const expectedHeaderBottom = () => Math.max(visibleHeaderBottom(), Math.ceil(headerGrid.offsetHeight || 0));

    const positionSearchDialog = () => {
      if (!searchOverlay || inlineSearch) return;
      const headerRect = header.getBoundingClientRect();
      const desiredTop = expectedHeaderBottom() + (MOBILE_QUERY.matches ? 12 : 22);
      const maximumTop = Math.max(16, window.innerHeight - (MOBILE_QUERY.matches ? 300 : 360));
      searchOverlay.style.setProperty('--rmx-search-overlay-top', `${Math.round(-headerRect.top)}px`);
      searchOverlay.style.setProperty('--rmx-search-modal-top', `${clamp(desiredTop, 16, maximumTop)}px`);
    };

    const setRootOffset = () => {
      offsetFrame = 0;
      document.documentElement.style.setProperty('--rmx-sticky-offset', `${visibleHeaderBottom()}px`);
      group.style.setProperty('--rmx-header-hide-distance', `${Math.ceil(headerGrid.offsetHeight || 86) + 4}px`);
      positionDesktopDropdowns();
      if (searchIsOpen()) positionSearchDialog();
    };

    const offsetTick = (time) => {
      setRootOffset();
      if (time < offsetTrackUntil) offsetFrame = window.requestAnimationFrame(offsetTick);
    };

    const trackOffset = (duration = 0) => {
      offsetTrackUntil = Math.max(offsetTrackUntil, performance.now() + duration);
      if (!offsetFrame) offsetFrame = window.requestAnimationFrame(offsetTick);
    };

    const menuIsOpen = () => MOBILE_QUERY.matches && header.classList.contains('rmx-mobile-menu-is-open');
    const searchIsOpen = () => inlineSearch
      ? Boolean(searchInput && (searchInput === document.activeElement || header.querySelector('.header__search predictive-search[open]')))
      : header.classList.contains('search-is-visible');
    const hoursAreOpen = () => Boolean(header.querySelector('.rmx-phone-status-control.is-open'));
    const headerIsEngaged = () => menuIsOpen() || searchIsOpen() || hoursAreOpen() || externalDrawerOpen;

    const showHeader = () => {
      const changed = group.classList.contains('is-rmx-header-hidden');
      group.classList.remove('is-rmx-header-hidden');
      if (changed) trackOffset(360);
    };

    const hideHeader = () => {
      if (!stickyEnabled || !smartSticky || REDUCED_MOTION.matches || headerIsEngaged()) return;
      if (group.classList.contains('is-rmx-header-hidden')) return;
      group.classList.add('is-rmx-header-hidden');
      trackOffset(360);
    };

    const syncEngagement = () => {
      const engaged = headerIsEngaged();
      group.classList.toggle('is-rmx-header-engaged', engaged);
      section.classList.toggle('is-rmx-mobile-menu-open', menuIsOpen());
      group.classList.toggle('is-rmx-mobile-menu-open', menuIsOpen());
      section.classList.toggle('is-rmx-external-drawer-open', externalDrawerOpen);
      if (engaged) showHeader();
      trackOffset(140);
    };

    const syncScrollState = (scrollY = window.scrollY) => {
      const atTop = Math.max(0, scrollY) <= 12;
      group.classList.toggle('is-rmx-at-top', atTop);
      group.classList.toggle('is-rmx-scrolled', !atTop);
      section.classList.toggle('is-rmx-at-top', atTop);
      section.classList.toggle('is-rmx-scrolled', !atTop);
      header.classList.toggle('is-rmx-scrolled', !atTop);
    };

    /* Deliberate direction thresholds avoid flicker from trackpads and small
       scroll corrections. */
    let lastScrollY = Math.max(0, window.scrollY);
    let direction = 0;
    let directionDistance = 0;
    let directionStartedAt = performance.now();
    let scrollFrame = 0;

    const resetDirection = () => {
      direction = 0;
      directionDistance = 0;
      directionStartedAt = performance.now();
    };

    const updateScroll = () => {
      scrollFrame = 0;
      const currentScrollY = Math.max(0, window.scrollY);
      const delta = currentScrollY - lastScrollY;
      lastScrollY = currentScrollY;
      syncScrollState(currentScrollY);
      trackOffset(60);

      if (!stickyEnabled || !smartSticky || REDUCED_MOTION.matches) {
        showHeader();
        resetDirection();
        return;
      }

      if (currentScrollY <= 12 || headerIsEngaged()) {
        showHeader();
        resetDirection();
        return;
      }

      if (Math.abs(delta) < 2) return;

      const nextDirection = delta > 0 ? 1 : -1;
      if (nextDirection !== direction) {
        direction = nextDirection;
        directionDistance = 0;
        directionStartedAt = performance.now();
      }

      directionDistance += Math.abs(delta);

      if (
        direction === 1 &&
        currentScrollY > Math.max(header.offsetHeight + 24, 140) &&
        directionDistance >= 72
      ) {
        hideHeader();
        resetDirection();
        return;
      }

      if (
        direction === -1 &&
        directionDistance >= 140 &&
        performance.now() - directionStartedAt >= 220
      ) {
        showHeader();
        resetDirection();
      }
    };

    const requestScrollUpdate = () => {
      if (!scrollFrame) scrollFrame = window.requestAnimationFrame(updateScroll);
    };

    window.addEventListener('scroll', requestScrollUpdate, { passive: true });
    window.addEventListener('resize', () => {
      lastScrollY = Math.max(0, window.scrollY);
      syncScrollState(lastScrollY);
      resetDirection();
      trackOffset(220);
      if (menuIsOpen()) updateMobileMenuMetrics();
    }, { passive: true });
    window.addEventListener('orientationchange', () => trackOffset(300), { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        if (menuIsOpen()) updateMobileMenuMetrics();
      }, { passive: true });
    }
    window.addEventListener('pageshow', () => {
      lastScrollY = Math.max(0, window.scrollY);
      syncScrollState(lastScrollY);
      resetDirection();
      if (window.scrollY <= 12) showHeader();
      trackOffset(240);
    });

    header.addEventListener('focusin', () => {
      showHeader();
      syncEngagement();
    });

    /* ---------------- Search ----------------
       Capture phase takes ownership before the base theme's delayed header
       search handler. This removes the intermediate white/unclickable state. */
    const setSearchOpen = (open, opener = null) => {
      if (!searchOverlay || inlineSearch) return;
      if (open) {
        searchOpener = opener || document.activeElement;
        showHeader();
        positionSearchDialog();
        trackOffset(380);
        header.classList.remove('search-is-collapsed');
        header.classList.add('search-is-visible');
        searchOverlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('rmx-v7-search-open');
        window.requestAnimationFrame(() => searchInput?.focus({ preventScroll: true }));
      } else {
        header.classList.remove('search-is-visible');
        header.classList.add('search-is-collapsed');
        searchOverlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('rmx-v7-search-open');
        if (searchOpener instanceof HTMLElement) searchOpener.focus({ preventScroll: true });
        searchOpener = null;
      }
      searchToggles.forEach((toggle) => toggle.setAttribute('aria-expanded', String(open)));
      syncEngagement();
    };

    if (searchOverlay && !inlineSearch) {
      searchOverlay.setAttribute('aria-hidden', searchIsOpen() ? 'false' : 'true');

      header.addEventListener('click', (event) => {
        const openButton = event.target.closest('.js-show-search, .js-show-search-left');
        const closeButton = event.target.closest('[data-rmx-search-close]');
        if (!openButton && !closeButton) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        setSearchOpen(Boolean(openButton), openButton);
      }, true);

      searchOverlay.addEventListener('click', (event) => {
        if (event.target === searchOverlay) setSearchOpen(false);
      });
    }

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (searchIsOpen()) setSearchOpen(false);
      if (menuIsOpen()) setMobileMenuOpen(false);
    });

    /* ---------------- Mobile menu ----------------
       The mobile drawer is intentionally independent from the theme's
       desktop <main-menu> custom element. This prevents the desktop mega-menu
       script from converting mobile submenus into overlapping fixed panels. */
    let previousHtmlOverflow = '';
    let previousBodyOverflow = '';

    const lockPage = () => {
      if (document.documentElement.classList.contains('rmx-page-lock')) return;
      previousHtmlOverflow = document.documentElement.style.overflow;
      previousBodyOverflow = document.body.style.overflow;
      document.documentElement.classList.add('rmx-page-lock', 'rmx-mobile-menu-open');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    };

    const unlockPage = () => {
      document.documentElement.classList.remove('rmx-page-lock', 'rmx-mobile-menu-open');
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousBodyOverflow;
    };

    function updateMobileMenuMetrics() {
      if (!mobileMenuLayer || !MOBILE_QUERY.matches) {
        header.style.removeProperty('--rmx-mobile-drawer-top');
        header.style.removeProperty('--rmx-mobile-drawer-height');
        return;
      }

      showHeader();
      const viewportHeight = Math.ceil(window.visualViewport?.height || window.innerHeight);
      const bottom = clamp(Math.ceil(headerGrid.getBoundingClientRect().bottom), 0, viewportHeight);
      const availableHeight = Math.max(0, viewportHeight - bottom);
      header.style.setProperty('--rmx-mobile-drawer-top', `${bottom}px`);
      header.style.setProperty('--rmx-mobile-drawer-height', `${availableHeight}px`);
    }

    const setMobileMenuOpen = (open, restoreFocus = true) => {
      if (!mobileMenuToggle || !mobileMenuLayer) return;
      const shouldOpen = Boolean(open && MOBILE_QUERY.matches);

      if (shouldOpen) {
        /* A sticky header can still be part-way through its reveal animation
           when the menu button becomes clickable. Pin its owning Shopify
           section before locking page scroll so the drawer cannot be left
           above the viewport while the page is inert. */
        showHeader();
        const headerTop = window.scrollY <= 12
          ? clamp(Math.ceil(group.getBoundingClientRect().top), 0, window.innerHeight)
          : 0;
        group.style.setProperty('--rmx-mobile-menu-header-top', `${headerTop}px`);
      }

      header.classList.toggle('rmx-mobile-menu-is-open', shouldOpen);
      mobileMenuToggle.setAttribute('aria-expanded', String(shouldOpen));
      mobileMenuToggle.setAttribute('aria-label', shouldOpen ? 'Close menu' : 'Open menu');
      mobileMenuLayer.setAttribute('aria-hidden', String(!shouldOpen));

      /* Apply the engaged state before measuring. The open state fixes the
         header and drawer to the viewport, independent of sticky scroll state. */
      syncEngagement();

      if (shouldOpen) {
        /* Force the fixed open-state styles to resolve before measuring and
           before overflow locking changes the scroll container. */
        group.getBoundingClientRect();
        updateMobileMenuMetrics();
        lockPage();
        window.requestAnimationFrame(() => {
          updateMobileMenuMetrics();
          const firstTarget = mobileMenuDrawer?.querySelector('a, summary, button');
          firstTarget?.focus({ preventScroll: true });
          window.requestAnimationFrame(updateMobileMenuMetrics);
        });
      } else {
        unlockPage();
        group.style.removeProperty('--rmx-mobile-menu-header-top');
        header.style.removeProperty('--rmx-mobile-drawer-top');
        header.style.removeProperty('--rmx-mobile-drawer-height');
        if (restoreFocus) mobileMenuToggle.focus({ preventScroll: true });
      }

      syncEngagement();
      trackOffset(180);
    };

    if (mobileMenuToggle && mobileMenuLayer) {
      mobileMenuToggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        setMobileMenuOpen(!menuIsOpen(), false);
      });

      mobileMenuLayer.addEventListener('click', (event) => {
        if (event.target.closest('[data-rmx-mobile-menu-close]')) {
          event.preventDefault();
          setMobileMenuOpen(false);
          return;
        }

        const destination = event.target.closest('a[href]');
        if (destination && !destination.closest('summary')) {
          setMobileMenuOpen(false, false);
        }
      });

      mobileMenuLayer.addEventListener('toggle', (event) => {
        const opened = event.target;
        if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;

        /* Keep one top-level group open at a time. Nested groups are scoped to
           their own parent panel and are not affected. */
        if (opened.classList.contains('rmx-mobile-nav-group')) {
          mobileMenuLayer.querySelectorAll('.rmx-mobile-nav-group[open]').forEach((group) => {
            if (group !== opened) group.removeAttribute('open');
          });
        }
      }, true);

      MOBILE_QUERY.addEventListener?.('change', () => {
        if (!MOBILE_QUERY.matches) setMobileMenuOpen(false, false);
        updateMobileMenuMetrics();
      });
    }

    const compactMenus = Array.from(header.querySelectorAll('details.rmx-compact-menu'));
    compactMenus.forEach((menu) => {
      new MutationObserver(() => {
        if (!MOBILE_QUERY.matches && menu.classList.contains('is-open')) {
          positionDesktopDropdowns();
        }
      }).observe(menu, { attributes: true, attributeFilter: ['class', 'open'] });

      menu.addEventListener('mouseenter', positionDesktopDropdowns, { passive: true });
      menu.addEventListener('focusin', positionDesktopDropdowns);
    });
    positionDesktopDropdowns();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        trackOffset(140);
        if (menuIsOpen()) updateMobileMenuMetrics();
      });
      resizeObserver.observe(header);
    }

    /* ---------------- Opening hours ---------------- */
    const controls = Array.from(header.querySelectorAll('.rmx-phone-status-control'));

    const closeHours = (control) => {
      const button = control.querySelector('[data-rmx-hours-toggle]');
      const panel = control.querySelector('[data-rmx-hours-panel]');
      if (!button || !panel) return;
      button.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      control.classList.remove('is-open');
    };

    const closeAllHours = (except = null) => {
      controls.forEach((control) => {
        if (control !== except) closeHours(control);
      });
      syncEngagement();
    };

    controls.forEach((control) => {
      const button = control.querySelector('[data-rmx-hours-toggle]');
      const panel = control.querySelector('[data-rmx-hours-panel]');
      if (!button || !panel) return;

      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const willOpen = button.getAttribute('aria-expanded') !== 'true';
        closeAllHours(control);
        button.setAttribute('aria-expanded', String(willOpen));
        panel.hidden = !willOpen;
        control.classList.toggle('is-open', willOpen);
        if (willOpen) showHeader();
        syncEngagement();
      });
      panel.addEventListener('click', (event) => event.stopPropagation());
    });

    document.addEventListener('click', () => closeAllHours());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeAllHours();
    });

    const updatePhoneStatuses = () => {
      const now = londonNow();
      header.querySelectorAll('[data-rmx-phone-status]').forEach((element) => {
        const openMinutes = parseTime(element.dataset.openTime, '09:00');
        const closeMinutes = parseTime(element.dataset.closeTime, '17:30');
        const warningMinutes = Math.max(0, Number(element.dataset.warningMinutes || 30));
        const closedForDay =
          (now.weekday === 'Sat' && element.dataset.openSaturday !== 'true') ||
          (now.weekday === 'Sun' && element.dataset.openSunday !== 'true');

        let state = 'closed';
        let label = element.dataset.closedLabel || 'Closed now';
        if (!closedForDay && now.minutes >= openMinutes && now.minutes < closeMinutes) {
          if (now.minutes >= closeMinutes - warningMinutes) {
            state = 'closing';
            label = element.dataset.closingLabel || 'Closing soon';
          } else {
            state = 'open';
            label = element.dataset.openLabel || 'Open now';
          }
        }

        element.dataset.state = state;
        element.querySelectorAll('.rmx-phone-status__label').forEach((labelElement) => {
          labelElement.textContent = label;
        });
      });
    };

    updatePhoneStatuses();
    window.setInterval(updatePhoneStatuses, 60000);

    /* Cart and quick-buy drawers sit above the header and suspend auto-hide.
       On mobile the site menu and a modal drawer must never remain open together. */
    document.addEventListener('on:cart-drawer:before-open', () => {
      if (menuIsOpen()) setMobileMenuOpen(false, false);
      if (searchIsOpen()) setSearchOpen(false);
      closeAllHours();
    });

    const drawerIsModalOpen = () => Boolean(document.querySelector(
      '.drawer[open]:not(.facets), .drawer.facets[open][aria-modal="true"]'
    ));

    const syncExternalDrawers = () => {
      externalDrawerOpen = drawerIsModalOpen();
      syncEngagement();
    };

    new MutationObserver(syncExternalDrawers).observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ['open']
    });

    [
      'on:cart-drawer:before-open',
      'on:cart-drawer:after-open',
      'on:cart-drawer:after-close',
      'on:quickbuy:before-open',
      'on:quickbuy:after-open',
      'on:quickbuy:after-close'
    ].forEach((eventName) => document.addEventListener(eventName, syncExternalDrawers));

    syncScrollState(lastScrollY);
    syncExternalDrawers();
    setRootOffset();
    requestScrollUpdate();
  };

  const init = (root = document) => {
    if (root.matches?.(HEADER_SELECTOR)) initHeader(root);
    root.querySelectorAll?.(HEADER_SELECTOR).forEach(initHeader);
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
